import axios, { AxiosResponse } from 'axios';
import { formatDateToYYYYMMDD } from '../../../utils/dates';
import { apiClient } from '../apiClient';
import { Dump } from '../materials/dumps';
import { Truck } from '../materials/trucks';
import { Site } from '../sites/sites';
import { Quarry } from '../sites/quarries';
import { DailyTransferRide } from './dailyTransfersRides';
import { LoadNature } from '../materials/loadNatures';
import { User } from '../users';

export interface DailyTruckRide {
    _id: string;
    truckId: Truck;
    activityId: string;
    time?: string;
    mode?: number;
    nickname?: string;
    departureLoadNatureId?: LoadNature;
    arrivalLoadNatureId?: LoadNature;
    departureSiteId?: Site;
    arrivalSiteId?: Site;
    departureQuarryId?: Quarry;
    arrivalQuarryId?: Quarry;
    departureDumpId?: Dump;
    arrivalDumpId?: Dump;
    transfers: DailyTransferRide[];
    observations?: string;
    departureTime?: string;
    arrivalTime?: string;
    departureTonnage?: number;
    arrivalTonnage?: number;
    secondTime?: string;
    includedInExpenses?: boolean;
    price?: number;
    priceType?: number;
    isDecommissioned?: boolean;
    isLongDistance?: boolean;
    longDistanceCoefficient?: number;
}

interface DailyTrucksRidesResponse {
    message: string;
    data: DailyTruckRide[];
}

export interface GetDailyTrucksRidesQueries {
    date?: string;
    stepMode?: number;
    mode?: number;
}

export const getDailyTrucksRides = async (query: GetDailyTrucksRidesQueries): Promise<DailyTrucksRidesResponse> => {
    try {
        const response: AxiosResponse<DailyTrucksRidesResponse> = await apiClient.get('/daily-trucksrides/', { params: query });
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface GetTonnageByDriverQuery {
    startDate?: string; // format ISO, ex: '2025-08-01'
    endDate?: string; // format ISO
}

export interface TonnageByMode {
    mode: number;
    total: number;
}

export interface TonnageByDriver {
    user: User;
    userId: string;
    departureByMode: TonnageByMode[];
    arrivalByMode: TonnageByMode[];
}

export interface TonnageByDriverResponse {
    message: string;
    data: TonnageByDriver[];
}

export const getTonnageByDriver = async (queries: GetTonnageByDriverQuery): Promise<TonnageByDriverResponse> => {
    try {
        const response: AxiosResponse<TonnageByDriverResponse> = await apiClient.get('/daily-trucksrides/tonnage/drivers', {
            params: queries,
        });
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface CreateDailyTruckRideWithPlateBody {
    activityId: string;
    plateNumber: string;
    truckCompanyId: string;
    truckTypeId?: string | null;
    time?: string | null;
    secondTime?: string | null;
    departureLoadNatureId?: string | null;
    arrivalLoadNatureId?: string | null;
    departureSiteId?: string | null;
    arrivalSiteId?: string | null;
    departureQuarryId?: string | null;
    arrivalQuarryId?: string | null;
    departureDumpId?: string | null;
    arrivalDumpId?: string | null;
    secondLoadNatureId?: string | null;
    secondSiteId?: string | null;
    secondQuarryId?: string | null;
    mode?: number | null;
    nickname?: string | null;
    observations?: string | null;
    departureTime?: string | null;
    arrivalTime?: string | null;
    departureTonnage?: number | null;
    arrivalTonnage?: number | null;
    isDecommissioned?: boolean;
    isIncomplete: boolean;
    isLongDistance?: boolean;
    longDistanceCoefficient?: number | null;
    includedInExpenses?: boolean;
}

export interface CreateDailyTruckRideBody {
    activityId: string;
    truckId: string;
    time?: string | null;
    secondTime?: string | null;
    departureLoadNatureId?: string | null;
    arrivalLoadNatureId?: string | null;
    departureSiteId?: string | null;
    arrivalSiteId?: string | null;
    departureQuarryId?: string | null;
    arrivalQuarryId?: string | null;
    departureDumpId?: string | null;
    arrivalDumpId?: string | null;
    secondLoadNatureId?: string | null;
    secondSiteId?: string | null;
    secondQuarryId?: string | null;
    mode?: number;
    nickname?: string | null;
    observations?: string | null;
    departureTime?: string | null;
    arrivalTime?: string | null;
    departureTonnage?: number | null;
    arrivalTonnage?: number | null;
    isDecommissioned?: boolean;
    isIncomplete: boolean;
    longDistanceCoefficient?: number | null;
    includedInExpenses?: boolean;
}

export interface DailyTruckRideResponse {
    message: string;
    data: DailyTruckRide;
}

export interface UpdateDailyTruckRideBody {
    activityId?: string;
    truckId?: string;
    time?: string | null;
    secondTime?: string | null;
    departureLoadNatureId?: string | null;
    arrivalLoadNatureId?: string | null;
    departureSiteId?: string | null;
    arrivalSiteId?: string | null;
    departureQuarryId?: string | null;
    arrivalQuarryId?: string | null;
    departureDumpId?: string | null;
    arrivalDumpId?: string | null;
    secondLoadNatureId?: string | null;
    secondSiteId?: string | null;
    secondQuarryId?: string | null;
    mode?: number | null;
    nickname?: string | null;
    observations?: string | null;
    departureTime?: string | null;
    arrivalTime?: string | null;
    departureTonnage?: number | null;
    arrivalTonnage?: number | null;
    isDecommissioned?: boolean;
    isLongDistance?: boolean;
    longDistanceCoefficient?: number | null;
    includedInExpenses?: boolean;
}

export const createDailyTrucksRides = async (props: CreateDailyTruckRideBody): Promise<DailyTruckRideResponse> => {
    try {
        const response: AxiosResponse<DailyTruckRideResponse> = await apiClient.post('/daily-trucksrides/normal', props);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export const updateDailyTrucksRides = async (id: string, props: UpdateDailyTruckRideBody): Promise<DailyTruckRideResponse> => {
    try {
        const response: AxiosResponse<DailyTruckRideResponse> = await apiClient.patch(`/daily-trucksrides/${id}`, props);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export const deleteDailyTrucksRides = async (id: string): Promise<DailyTruckRideResponse> => {
    try {
        const response: AxiosResponse<DailyTruckRideResponse> = await apiClient.delete('/daily-trucksrides/' + id);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface UpdateIncludedInExpensesBody {
    ids: string[];
    includedInExpenses: boolean;
}

export interface UpdateIncludedInExpensesResponse {
    message: string;
    data: {
        modifiedCount: number;
    };
}

export const updateIncludedInExpenses = async (props: UpdateIncludedInExpensesBody): Promise<UpdateIncludedInExpensesResponse> => {
    try {
        const response: AxiosResponse<UpdateIncludedInExpensesResponse> = await apiClient.patch('/daily-trucksrides/expenses', props);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};
